import { UilCheckCircle } from '@iconscout/react-unicons';
import Unicon from 'components/base/Unicon';
import { Card, Col, Container, Row } from 'react-bootstrap';
import bg37 from 'assets/img/bg/37.png';
import bg38 from 'assets/img/bg/38.png';
import authCarrierDataflow from 'assets/img/spot-illustrations/auth-carrier-dataflow.png';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import Logo from 'components/common/Logo';
import classNames from 'classnames';

interface AuthCardLayoutProps {
  logo?: boolean;
  className?: string;
  showPromo?: boolean;
}

const AuthCardLayout = ({
  logo = true,
  className,
  showPromo = true,
  children
}: PropsWithChildren<AuthCardLayoutProps>) => {
  return (
    <Container fluid className="bg-300 dark__bg-1200">
      <div
        className="bg-holder bg-auth-card-overlay"
        style={{ backgroundImage: `url(${bg37})` }}
      />

      <Row className="flex-center position-relative min-vh-100 g-0 py-5">
        <Col
          xs={11}
          sm={showPromo ? 10 : 8}
          md={showPromo ? undefined : 6}
          lg={showPromo ? undefined : 5}
          xl={showPromo ? 8 : 4}
        >
          <Card className="border border-200 auth-card">
            <Card.Body className={showPromo ? 'pe-md-0' : undefined}>
              <Row className="align-items-center gx-0 gy-7">
                {showPromo && (
                  <Col
                    xs="auto"
                    className="bg-100 dark__bg-1100 rounded-3 position-relative overflow-hidden auth-title-box"
                  >
                    <div
                      className="bg-holder"
                      style={{ backgroundImage: `url(${bg38})` }}
                    />
                    <div
                      className={classNames(
                        className,
                        'position-relative px-4 px-lg-7 py-7 pb-sm-5 text-center text-md-start pb-lg-7'
                      )}
                    >
                      <h3 className="mb-3 text-black fs-7">
                        Carrier Billing Data, Delivered.
                      </h3>
                      <p className="text-700 mb-4">
                        Access your carrier billing data — including shipment
                        and charge-level detail — directly from the source.
                      </p>
                      <ul className="list-unstyled mb-4 w-max-content w-md-auto mx-auto">
                        <li className="d-flex align-items-center gap-2">
                          <Unicon
                            icon={UilCheckCircle}
                            className="text-success"
                            size={16}
                          />
                          <span className="text-700 fw-semi-bold">
                            Automated at the source
                          </span>
                        </li>
                        <li className="d-flex align-items-center gap-2">
                          <Unicon
                            icon={UilCheckCircle}
                            className="text-success"
                            size={16}
                          />
                          <span className="text-700 fw-semi-bold">
                            API and S3 delivery
                          </span>
                        </li>
                        <li className="d-flex align-items-center gap-2">
                          <Unicon
                            icon={UilCheckCircle}
                            className="text-success"
                            size={16}
                          />
                          <span className="text-700 fw-semi-bold">
                            Always accessible, always yours
                          </span>
                        </li>
                      </ul>
                      <p className="text-700 mb-0 fw-semi-bold">
                        Built for brands, 3PLs, and logistics platforms
                      </p>
                    </div>
                    <div className="position-relative mb-6 d-none d-md-block text-center mt-md-15 z-index--1">
                      <img
                        className="auth-title-box-img"
                        style={{ objectFit: 'contain' }}
                        src={authCarrierDataflow}
                        alt="Carrier billing data flows from major carriers through an API layer to cloud storage and Amazon S3"
                      />
                    </div>
                  </Col>
                )}
                <Col className="mx-auto">
                  {logo && (
                    <div className="text-center">
                      <Link
                        to="/"
                        className="d-inline-block text-decoration-none mb-4"
                      >
                        <Logo
                          text={false}
                          width={58}
                          className="fw-bolder fs-5 d-inline-block"
                        />
                      </Link>
                    </div>
                  )}
                  <div className="auth-form-box">{children}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AuthCardLayout;
